import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import CommonImage from '@components/CommonImage';
import CustomTooltip, { POSITIONS } from '@components/CustomTooltip';
import SearchebleSelect from '@components/SearchebleSelect';
import { toastSuccess, toastWarning } from '@components/Toast';

import useClickOutside from '@hooks/useClickOutside';

import { getUpdatedProductList } from '@features/favourites/utils/favouriteUtil';
import { getFilterTextFieldValue } from '@features/filter/static/filterUtil';

import { removeItemFromGlobalFilter, updateTextItemInGlobalFilter } from '@store/actions/FilterAction';
import { likeProduct } from '@store/design/action';
import { getFavouriteProducts } from '@store/reducers';
import {
	getSearchResult,
	getSelectedSearchType,
	toggleEditPresentation,
	toggleFavourite,
	toggleModalClose,
	toggleSampleId,
	toggleViewTechpack
} from '@store/searchOptions/actions';

import {
	fetchProductRefferenceNumber,
	fetchProductSearchHistory,
	saveProductSearchHistory,
	setSystemUserPreferences
} from '@services/commonService';

import { getUserPreference } from '@utils/Util';

const IDENTIFIER = 'referenceNumber';

const AsyncSearch = ({
	isHideChip = false,
	pathName,
	folderId,
	parentComponent,
	viewType,
	setQRLikedDesignIds,
	setIsShowFavouriteFolderPopup,
	shouldUpdateProductList = false
}) => {
	const dispatch = useDispatch();
	const {
		isAddToFavourite,
		isViwTechpack,
		isUpdateSampleId,
		searchType,
		isFocusOnModalClose,
		isEditPresentation,
		isTechpackOpen
	} = useSelector((store) => store.collectionSearchOptionStore);

	const [listOfData, setListOfData] = useState([]);
	const [searchTerm, setSearchTerm] = useState('');
	const [isLoading, setIsLoading] = useState(false);
	const [qrCode, setQrCode] = useState('');
	const [isShowDropdown, setIsShowDropdown] = useState(false);

	const filterData = useSelector((store) => store.filterFieldStore);
	const allFavouritesForProducts = useSelector(getFavouriteProducts);
	const inputData = getFilterTextFieldValue(filterData, IDENTIFIER, IDENTIFIER);

	const searchRef = useRef();
	const inputRef = useRef();
	const dropdownRef = useRef(null);

	useClickOutside(dropdownRef, () => setIsShowDropdown(false));
	useClickOutside(searchRef, () => setSearchTerm(''));

	useEffect(() => {
		const inputField = document.getElementById('QR-code');
		if (inputField) {
			inputField.focus();
		}
	}, [searchType, pathName, isTechpackOpen]);

	const handleUserPreferenceUpdate = async (preferenceKey, preferenceValue, toggleAction) => {
		dispatch(toggleAction(!preferenceValue));
		const userPreference = getUserPreference();
		await setSystemUserPreferences({
			...userPreference,
			[preferenceKey]: !preferenceValue
		});
	};

	const handleSearchType = async (value) => {
		dispatch(getSelectedSearchType(value));
		await setSystemUserPreferences({
			...getUserPreference(),
			SEARCH_TYPE: value
		});
	};

	const handleEditPresentation = async () => {
		const userPreference = getUserPreference();
		const preferencesToDisable = [
			{ key: 'IS_VIEW_TECHPACK', action: toggleViewTechpack, value: isViwTechpack },
			{ key: 'IS_UPDATE_SAMPLE_ID', action: toggleSampleId, value: isUpdateSampleId },
			{ key: 'IS_FOCUS_ON_MODAL_CLOSE', action: toggleModalClose, value: isFocusOnModalClose }
		];

		for (const { key, action, value } of preferencesToDisable) {
			if (value) {
				dispatch(action(false));
				await setSystemUserPreferences({
					...userPreference,
					[key]: false
				});
			}
		}

		handleUserPreferenceUpdate('IS_EDIT_PRESENTATION', isEditPresentation, toggleEditPresentation);
	};

	useEffect(() => {
		setSearchTerm(parentComponent === 'SidePanel' ? '' : inputData?.value || '');
		return () => setSearchTerm('');
	}, [inputData, parentComponent]);

	useEffect(() => {
		if (searchType === 'DESIGN_NO' || searchType === 'BUYER_DESIGN_NO') {
			setIsLoading(true);
			const timeout = setTimeout(() => {
				searchTerm?.length > 0 ? loadOptions() : loadSearchHistory();
			}, 500);
			return () => clearTimeout(timeout);
		}
	}, [searchTerm, searchType]);

	const loadOptions = async () => {
		try {
			const response = await fetchProductRefferenceNumber(encodeURIComponent(searchTerm));
			const filtered = response?.data?.filter((option) =>
				searchType === 'DESIGN_NO'
					? option?.systemReferenceNumber?.toLowerCase().includes(searchTerm?.toLowerCase())
					: option?.label?.toLowerCase().includes(searchTerm?.toLowerCase())
			);
			setListOfData(filtered);
		} finally {
			setIsLoading(false);
		}
	};

	const loadSearchHistory = async () => {
		try {
			const response = await fetchProductSearchHistory();
			const filtered = response?.data?.filter((option) =>
				searchType === 'DESIGN_NO'
					? option?.systemReferenceNumber?.toLowerCase().includes(searchTerm?.toLowerCase())
					: option?.label?.toLowerCase().includes(searchTerm?.toLowerCase())
			);
			setListOfData(filtered);
		} finally {
			setIsLoading(false);
		}
	};

	const isProductAlreadyInFavourites = (productId) => {
		return allFavouritesForProducts?.data?.some(
			(product) => parseInt(product?.productResponse?.id) === parseInt(productId)
		);
	};

	const onSuccessLikeProduct = (response) => {
		if (shouldUpdateProductList) {
			const isProductAlreadyInFav = isProductAlreadyInFavourites(
				response?.payload?.LikedProductList[0]?.productResponse?.id
			);
			if (isProductAlreadyInFav) {
				toastWarning('Product already in favourites');
				return;
			}
			const updatedProductList = getUpdatedProductList(allFavouritesForProducts, response);
			dispatch({
				type: 'FETCH_FAVOURITE_PRODUCT_LIST_SUCCESS',
				payload: {
					totalElements: allFavouritesForProducts?.totalElements + 1,
					totalPages: Math.ceil((allFavouritesForProducts?.totalElements + 1) / 30),
					currentPage: Math.ceil((allFavouritesForProducts?.totalElements + 1) / 30),
					data: [...response?.payload?.LikedProductList, ...updatedProductList]
				}
			});
		}

		setQRLikedDesignIds?.((prev) => [
			...prev,
			response?.payload?.LikedProductList[0]?.productResponse?.id
		]);
		toastSuccess('Added to favourite');
	};

	const onSelect = (item) => {
		const data = {
			name: IDENTIFIER,
			type: 'text',
			key: encodeURIComponent(item?.label),
			value: item?.label,
			identifier: IDENTIFIER,
			isHideChip,
			isAsyncSearch: true
		};

		if (isAddToFavourite && item?.value) {
			dispatch(likeProduct(item?.value, onSuccessLikeProduct, viewType, folderId));
			setIsShowFavouriteFolderPopup?.(true);
		}

		if (isViwTechpack) {
			dispatch(getSearchResult(parseInt(item?.value)));
		}

		dispatch(updateTextItemInGlobalFilter(data));
	};

	const getFormattedQRcode = (input) => {
		const value = input.target.value;
		setQrCode(value);

		const designIdPattern = /^(\d+);(.+);/;
		const sampleIdFirstPattern = /^\d+$/;
		const sampleIdSecondPattern = /^[A-Za-z0-9-]+;(\d+)$/;

		if (designIdPattern.test(value)) {
			return { designId: value.split(';')[0].trim() };
		} else if (sampleIdFirstPattern.test(value)) {
			return { sampleId: value.trim() };
		} else if (sampleIdSecondPattern.test(value)) {
			return { sampleId: value.split(';')[1].trim() };
		}

		return {};
	};

	const handleQRCodeSearch = async (e) => {
		if (e.key !== 'Enter') return;

		const { designId, sampleId } = getFormattedQRcode(e);
		const FILTER_BY = designId ? 'productIdList' : sampleId ? 'sampleIdentificationNo' : '';
		const value = designId || sampleId || '';

		if (!value) return;

		const data = {
			name: FILTER_BY,
			type: 'text',
			key: encodeURIComponent(value),
			value,
			identifier: FILTER_BY,
			isHideChip
		};

		if (isAddToFavourite && designId) {
			dispatch(likeProduct(designId, onSuccessLikeProduct, viewType, folderId));
			setIsShowFavouriteFolderPopup?.(true);
		}

		if (isViwTechpack && designId) {
			dispatch(getSearchResult(parseInt(designId)));
		}

		if (isEditPresentation && designId) {
			window.open(`/designs/edit/${designId}?activeTab=presentation`, '_blank');
		}

		dispatch(updateTextItemInGlobalFilter(data));
		dispatch(
			removeItemFromGlobalFilter({
				name: designId ? 'sampleIdentificationNo' : 'productIdList',
				identifier: designId ? 'sampleIdentificationNo' : 'productIdList'
			})
		);

		setQrCode('');
	};

	const renderSearchInput = () => {
		if (searchType === 'DESIGN_NO' || searchType === 'BUYER_DESIGN_NO') {
			return (
				<SearchebleSelect
					isLoading={isLoading}
					options={listOfData}
					onSelect={onSelect}
					onSearch={setSearchTerm}
					searchTerm={searchTerm}
					setSearchTerm={setSearchTerm}
					placeholder={searchType === 'DESIGN_NO' ? 'Nitex design no' : 'Buyer design no'}
					saveSearchForHistory={saveProductSearchHistory}
					selectedSearchOption={searchType}
					isTechpackOpen={isTechpackOpen}
					searchType={searchType}
				/>
			);
		}

		if (searchType === 'QR_CODE') {
			return (
				<div>
					<input
						value={qrCode}
						onChange={(e) => setQrCode(e.target.value)}
						onKeyDown={handleQRCodeSearch}
						type="text"
						name="QR-code"
						id="QR-code"
						placeholder="QR Code"
						ref={inputRef}
					/>
				</div>
			);
		}

		return null;
	};

	const renderDropdownItem = (text, isToggle, isEnabled, onToggle, tooltipText) => (
		<li className="d-flex align-items-center border-top-solid justify-content-between toggle-and-content">
			<span className="gray_dark_01 regular-14">{text}</span>
			{isToggle && (
				<>
					{tooltipText && !isEnabled && isEditPresentation ? (
						<CustomTooltip text={tooltipText} position={POSITIONS.TOP} arrowCenter={true}>
							<CommonImage
								className="cursor-pointer pr-3"
								src={`/icons/toggle-${isEnabled ? 'on' : 'off'}.svg`}
								alt="Toggle"
								onClick={onToggle}
							/>
						</CustomTooltip>
					) : (
						<CommonImage
							className="cursor-pointer pr-3"
							src={`/icons/toggle-${isEnabled ? 'on' : 'off'}.svg`}
							alt="Toggle"
							onClick={onToggle}
						/>
					)}
				</>
			)}
		</li>
	);

	return (
		<div
			className="form-group size32 m-0 search-filter filter-with-animation d-flex align-items-center"
			ref={searchRef}
		>
			<div className="with-icon right-icon">{renderSearchInput()}</div>

			<div className="edit-items search-edit-items">
				<div className="dropdown">
					<button
						className="btn btn-default dropdown-toggle d-flex align-items-center gap-2 show"
						onClick={() => setIsShowDropdown((prev) => !prev)}
					>
						<CommonImage src="/icons/search-dark.svg" />
					</button>

					{isShowDropdown && (
						<ul className="dropdown-menu dropdown-menu-right shadow-lg show" ref={dropdownRef}>
							<li
								onClick={() => handleSearchType('DESIGN_NO')}
								className={`${searchType === 'DESIGN_NO' ? 'active' : ''}`}
							>
								<span className="gray_dark_01 regular-14">Search Nitex Design No.</span>
							</li>
							<li
								onClick={() => handleSearchType('BUYER_DESIGN_NO')}
								className={`${searchType === 'BUYER_DESIGN_NO' ? 'active' : ''}`}
							>
								<span className="gray_dark_01 regular-14">Search Buyer Design No.</span>
							</li>
							<li
								onClick={() => handleSearchType('QR_CODE')}
								className={`${searchType === 'QR_CODE' ? 'active' : ''}`}
							>
								<span className="gray_dark_01 regular-14">Search with QR code</span>
							</li>

							{renderDropdownItem('Add to favourite', true, isAddToFavourite, () =>
								handleUserPreferenceUpdate(
									'IS_ADD_TO_FAVOURITE',
									isAddToFavourite,
									toggleFavourite
								)
							)}

							{renderDropdownItem(
								'View Techpack',
								true,
								isViwTechpack,
								!isEditPresentation
									? () =>
											handleUserPreferenceUpdate(
												'IS_VIEW_TECHPACK',
												isViwTechpack,
												toggleViewTechpack
											)
									: null,
								'You Need to toggle Edit Presentation'
							)}

							{renderDropdownItem(
								'Update Sample ID',
								true,
								isUpdateSampleId,
								!isEditPresentation
									? () =>
											handleUserPreferenceUpdate(
												'IS_UPDATE_SAMPLE_ID',
												isUpdateSampleId,
												toggleSampleId
											)
									: null,
								'You Need to toggle Edit Presentation'
							)}

							{renderDropdownItem(
								'Modal close-ABIU',
								true,
								isFocusOnModalClose,
								!isEditPresentation
									? () =>
											handleUserPreferenceUpdate(
												'IS_FOCUS_ON_MODAL_CLOSE',
												isFocusOnModalClose,
												toggleModalClose
											)
									: null,
								'You Need to toggle Edit Presentation'
							)}

							{renderDropdownItem('Edit presentation', true, isEditPresentation, () =>
								handleEditPresentation()
							)}
						</ul>
					)}
				</div>
			</div>
		</div>
	);
};

export default AsyncSearch;
